import React, {useContext, useEffect, useRef, useState} from "react";
import {useQuery} from '@tanstack/react-query';
import styled from "styled-components";
import * as vars from "../../shared/variables";
import Icon from "../Icon";
import {DebounceInput} from "react-debounce-input";
import Link from 'next/link';
import {useRouter} from "next/router";
import {getNavPath} from "../../shared/navigateUtil";
import {useCountryLang} from "../../hooks/useCountryLang";
import {useMessages} from "../../hooks/useMessages";
import {SearchTermContext} from "../../contexts/SearchTermContext";
import {ISearchResponse} from "../../interfaces/ISearchResponse";
import Highlighter from "react-highlight-words";
import {clearAllBodyScrollLocks, disableBodyScroll} from 'body-scroll-lock';
import {request} from "../../shared/httpInterceptor";
import {useKeyDown} from "../../hooks/useKeydown";
import {Spinner} from "react-bootstrap";
import Offcanvas from "react-bootstrap/Offcanvas";
import baseTheme from "../../shared/baseTheme";
import {getTheme} from "../../shared/themeUtils";
import Badge from "../Badge";
import parse from "html-react-parser";
import {getMappedStockColor} from "../../shared/stockColorMapUtil";
import Tag from "../Tag";

const Container = styled.div`
    height: 100%;
    width: 100%;
    display: flex;
    align-items: center;

    .tag {
        position: relative;
    }

    hr {
        color: ${getTheme("colors.border")};
    }

    .normal-search {
        width: 100%;
    }

    .mobile-search {
        display: none;
    }

    @media (max-width: ${getTheme("breakpoints.1")}) {
        .normal-search {
            display: none;
        }

        .mobile-search {
            display: inline-block;
            width: 100%;
        }
    }

    .mask {
        background: #000;
        opacity: .2;
        position: absolute;
        height: 115vh;
        width: 100%;
        top: -60px;
        right: 0;
        bottom: 0;
        left: 0;
        z-index: 10;
        display: flex;
        align-content: center;
    }

    .term-highlight {
        background: #fff;
    }
`

const SearchBox = styled.div`
    z-index: 100;
    width: 100%;
    max-width: 500px;
    position: relative;
    margin: 0 auto;

    @media (max-width: ${getTheme("breakpoints.1")}) {
        max-width: 100%;

        & > div {
            border: none;
            position: relative;

            .search-icon {
                margin: 0;
            }

            .search-input {
                position: absolute;
                top: 0;
                left: 0;
                width: 100%;
                height: 100%;
                cursor: pointer;
                font-size: 0;
            }
        }
    }
`

const SearchContain = styled.div`
    background: ${getTheme("colors.background")};
    border-radius: 30px;
    width: 100%;
    display: flex;

    .submit-search {
        border: none;
        border-right: 1px solid ${getTheme("colors.darkGrey")};
        border-top-left-radius: 20px;
        border-bottom-left-radius: 20px;
        padding: 0 ${getTheme("space.2")};
        background: ${getTheme("colors.background")};
        transition: background .3s;

        &:hover {
            background: ${getTheme("colors.primary")};
        }
    }

    .search-icon {
        margin-top: 10px;
        margin-left: 12px;
    }

    .search-input {
        background-color: transparent;
        color: ${getTheme("colors.primary")};
        font-size: 16px;
        padding: 10px 15px;
        outline: none;
        width: 100%;
        border: none;
        position: relative;

        &::placeholder {
            color: ${getTheme("colors.placeholder")};
        }
    }

    .clear {
        border: none;
        background: transparent;
        margin-right: 10px;
    }

    .loading {
        display: flex;
        align-items: center;
        padding: 0 ${getTheme("space.2")};
        color: ${getTheme("colors.secondaryFont")};
    }
`

const ResultsContain = styled.div`
    z-index: 20;
    position: absolute;
    width: 100%;
    max-height: 90vh;
    max-width: 600px;
    left: 0;
    right: 0;
    top: ${getTheme("space.2")};
    margin: 0 auto;
    background: #fff;
    color: ${getTheme("colors.primary")};
    padding: ${getTheme("space.3")};
    border-radius: 20px;
    box-shadow: 0 1px 70px 20px rgb(0 0 0 / 30%);

    label {
        font-size: ${getTheme("fontSizes.small")};
        color: ${getTheme("colors.secondaryFont")};
    }

    .items {
        margin-top: ${getTheme("space.2")};
        overflow-y: scroll;
        max-height: 70vh;

        .badge-overlay {
            background: ${getTheme("colors.accent")};
            color: ${getTheme("colors.white")};
            padding: ${getTheme("space.0")};
            border-radius: ${getTheme("space.2")};
            font-size: ${getTheme("fontSizes.xSmall")};
            font-weight: bold;
            position: absolute;
            bottom: 0;
            left: ${getTheme("space.1")};
        }

        ul {
            padding: 0;

            li {
                list-style: none;
                cursor: pointer;
                margin-bottom: ${getTheme("space.1")};
                display: flex !important;
                align-items: center;
                padding: ${getTheme("space.0")} 0;

                .img-contain {
                    width: 20%;
                    height: 50px;
                    position: relative;
                    margin-right: ${getTheme("space.2")};
                    display: flex;
                    justify-content: center;
                    align-items: center;

                    img {
                        max-width: 70px;
                        max-height: 100%;

                    }
                }

                .product-info {
                    display: flex;
                    flex-direction: column;
                    width: 80%;

                    .product-price {
                        color: ${getTheme("colors.secondaryFont")};
                        font-weight: bold;
                        margin-right: ${getTheme("space.1")};
                    }
                }
            }
        }
    }
`

const MobileSearch = styled.div`
    display: flex;
    align-items: center;
    background: ${getTheme("colors.background")};
    border-radius: 30px;
    padding: ${getTheme("space.0")} ${getTheme("space.1")};
    width: 100%;

    input {
        width: 100%;
        background: transparent;
        border: none;
        color: ${getTheme("colors.secondaryFont")};
        margin-left: ${getTheme("space.0")};
        outline: none;
        font-size: 16px;
    }

    .offcanvas-search > div {
        position: relative !important;
    }
`

type SearchPropsType = {
    isMobileSearch?: boolean;
}

const SearchAutocomplete = (props: SearchPropsType) => {
    const keyDownHook = useKeyDown(() => {
        handleKeydownEsc();
    }, ["Escape"])
    const [showSearch, setShowSearch] = useState(false)
    const [showMobileSearch, setShowMobileSearch] = useState(false)
    const router = useRouter()
    const countryLang = useCountryLang()
    const {searchTermCtx, setSearchTermCtx} = useContext(SearchTermContext)
    const [isLoading, setIsLoading] = useState(false)
    const messagesQuery = useMessages()
    const searchInputRef = useRef<HTMLInputElement>(null);

    // Determine if we should enable the autocomplete API call
    const shouldEnableAutocomplete = (showSearch || showMobileSearch) && 
                                     countryLang != null && 
                                     searchTermCtx.length > 2;

    useEffect(() => {
        if (router && router.query.q) {
          setSearchTermCtx(decodeURI(router.query.q.toString()))
        }

        router.events.on("routeChangeStart", () => {
            setShowSearch(false)
            setShowMobileSearch(false)
        })

        if (showMobileSearch) {
            setTimeout(() => {
                searchInputRef.current?.focus();
            }, 500);
        }

        return () => {
            clearAllBodyScrollLocks();
            setSearchTermCtx("");
        };
    }, [router, messagesQuery.data, showMobileSearch]);

    const handleKeydownEsc = () => {
        clearAllBodyScrollLocks();
        setShowSearch(false);
    }

    const fetchAutocomplete = async () => {
        setIsLoading(true);
        try {
            const res = await request({
              url: `/${countryLang}/search-autocomplete/${encodeURIComponent(searchTermCtx)}`,
                method: 'get'
            });
            setIsLoading(false);
            return res.data;
        } catch (error) {
            console.error('Error fetching autocomplete data:', error);
            setIsLoading(false);
            return [];
        }
    }

    const autocompleteQuery = useQuery<ISearchResponse>(
      ["autocomplete", searchTermCtx, countryLang], 
      fetchAutocomplete,
      {
        enabled: shouldEnableAutocomplete,
        // Don't refetch on window focus to reduce unnecessary API calls
        refetchOnWindowFocus: false,
      }
    );

    const handleChange = (e: any) => {
      const term = e.target.value;
        setSearchTermCtx(term);
    }

    const handleClearSearchTerm = async () => {
        setSearchTermCtx("");
    }

    const handleKeydownSubmit = async (event: any) => {
        if (event.key === 'Enter' && event.target.value.length > 2) {
            setShowSearch(false);
            const query = getQueryString(event.target.value.trim());
            await router.push(`/${countryLang}/search?${query}`);
        }
    }

    const handleButtonSubmit = async (event: any) => {
        setShowSearch(false);
        const query = getQueryString(searchTermCtx.trim());
        await router.push(`/${countryLang}/search?${query}`);
    }

    const getQueryString = (term: string) => {
        const paramsArr = [];
      paramsArr.push(`q=${encodeURIComponent(term)}`);
        if (router.query.sort) {
            paramsArr.push(`sort=${router.query.sort.toString()}`);
        }
        return paramsArr.join("&");
    }

    const searchBox = (
        <SearchContain>
          <button className="submit-search"
                  disabled={searchTermCtx.length < 3}
                  onClick={handleButtonSubmit}>
            <Icon
                color={vars.borderColor} variant={"medium"}>search-line</Icon></button>
            <DebounceInput 
                inputRef={searchInputRef}
                type="text"
                debounceTimeout={1000}
                min={3}
                placeholder={messagesQuery.data?.messages["search"]}
                onChange={handleChange}
                onKeyDown={handleKeydownSubmit}
                value={searchTermCtx}
                className="search-input"
                test-data="search-input"
                autoFocus={true}
            />
            {(searchTermCtx && !isLoading) && <button onClick={handleClearSearchTerm} className="clear">
                <Icon variant={"medium"} className={"close-icon"} color={"secondaryFont"}>close-line</Icon>
            </button>}
            {(searchTermCtx && isLoading) &&
                <div className="loading">
                    <Spinner animation="border" size="sm"/>
                </div>
            }
        </SearchContain>
    )

    const results = (
        <>
            {(autocompleteQuery.data && autocompleteQuery.data.productsCount > 0) &&
                <div className="items">
                    <div className="results products">
                        {messagesQuery.data &&
                            <label className="mb-1">{messagesQuery.data.messages["products"]}</label>}
                        <ul>
                            {autocompleteQuery.data.products.map(item => {
                                return (
                                    <Link
                                        href={getNavPath(router, `p/${item.slug}`)}
                                        key={item.productID}>
                                        <li>
                                            <div className='img-contain'>
                                                <img src={item.image.imageUrl}
                                                     alt={item.brandAndProductName}
                                                />
                                              {item.piecesOfMainImage && item.piecesOfMainImage &&
                                                <span className="badge-overlay">{item.piecesOfMainImage}x</span>}
                                            </div>
                                            <div className="product-info">
                                                <Highlighter
                                                    searchWords={searchTermCtx.split(" ")}
                                                    autoEscape={true}
                                                    textToHighlight={item.brandAndProductName}
                                                    className="product-name"
                                                />
                                              <div className="price-stock-section">
                                              <span className="product-price">{item.currencyPrice}</span>
                                              {item && item.stockDisplayText &&
                                                <Badge text={parse(item.stockDisplayText?.replace(/(<([^>]+)>)/gi, ""))}
                                                       type={getMappedStockColor(item.stockDisplayColor)} size="xSmall"
                                                       background={false}/>}
                                              </div>
                                              <span className={"tag"}>
                                                <Tag productTarget={item} type="product"/>
                                              </span>
                                            </div>
                                        </li>
                                    </Link>
                                );
                            })}
                        </ul>
                    </div>

                    {(autocompleteQuery.data.categoriesCount > 0) && <div className="results categories">
                      <hr/>
                      {messagesQuery.data &&
                        <label className="mb-1">{messagesQuery.data.messages["categories"]}</label>}
                      <ul>
                        {autocompleteQuery.data.categories.map((item: any) => {
                          return (
                            <Link
                              href={getNavPath(router, `cat/${item.slug}`)}
                              key={item.id}>
                              <li>
                                <Highlighter
                                  searchWords={searchTermCtx.split(" ")}
                                  autoEscape={true}
                                  textToHighlight={item.name}
                                />
                              </li>
                            </Link>
                          );
                        })}
                      </ul>
                    </div>}
                    {(autocompleteQuery.data.brandsCount > 0) && <div className="results brands">
                      <hr/>
                      {messagesQuery.data && <label className="mb-1">{messagesQuery.data.messages["brands"]}</label>}
                      <ul>
                        {autocompleteQuery.data.brands.map((item: any) => {
                          return (
                            <Link
                              href={getNavPath(router, `brand/${item.slug}`)}
                              key={item.brandID}>
                              <li>
                                <Highlighter
                                  searchWords={searchTermCtx.split(" ")}
                                  autoEscape={true}
                                  textToHighlight={item.brandName}
                                />
                              </li>
                            </Link>
                          );
                        })}
                      </ul>
                    </div>}
                  {(autocompleteQuery.data.seriesCount > 0) && <div className="results series">
                    <hr/>
                    {messagesQuery.data && <label className="mb-1">{messagesQuery.data.messages["series"]}</label>}
                    <ul>
                      {autocompleteQuery.data.series.map((item: any) => {
                        return (
                          <Link
                            href={getNavPath(router, `series/${item.slug}`)}
                            key={item.seriesID}>
                            <li>
                              <Highlighter
                                searchWords={searchTermCtx.split(" ")}
                                autoEscape={true}
                                textToHighlight={item.seriesFullName}
                              />
                            </li>
                          </Link>
                        );
                      })}
                    </ul>
                  </div>}
                  {(autocompleteQuery.data.subSeriesCount > 0) && <div className="results subSeries">
                    <hr/>
                    {messagesQuery.data && <label className="mb-1">{messagesQuery.data.messages["subseries"]}</label>}
                    <ul>
                      {autocompleteQuery.data.subSeries.map((item: any) => {
                        return (
                          <Link
                            href={getNavPath(router, `subseries/${item.slug}`)}
                            key={item.subSeriesID}>
                            <li>
                              <Highlighter
                                searchWords={searchTermCtx.split(" ")}
                                autoEscape={true}
                                textToHighlight={item.subSeriesFullName}
                              />
                            </li>
                          </Link>
                        );
                      })}
                    </ul>
                  </div>}
                  {(autocompleteQuery.data.magazineCount > 0) && <div className="results magazineArticles">
                    <hr/>
                    {messagesQuery.data && <label className="mb-1">{messagesQuery.data.messages["magazine-articles"]}</label>}
                    <ul>
                      {autocompleteQuery.data.magazines.map((item: any) => {
                        return (
                          <Link
                            href={getNavPath(router, `magazine/${item.slug}`)}
                            key={item.articleID}>
                            <li>
                              <Highlighter
                                searchWords={searchTermCtx.split(" ")}
                                autoEscape={true}
                                textToHighlight={item.title}
                              />
                            </li>
                          </Link>
                        );
                      })}
                    </ul>
                  </div>}
                </div>}
        </>
    );

    const dummySearchBox = (
        <SearchBox>
            <SearchContain>
                <Icon variant="medium" color={baseTheme.colors.darkestGrey} className="search-icon">search-line</Icon>
                <input type="text"
                       placeholder={messagesQuery.data?.messages["search"]}
                       onClick={() => {
                           setShowSearch(true);
                           setTimeout(() => {
                               const element: any = document.querySelector("#containModal");
                               disableBodyScroll(element);
                           }, 500);
                       }}
                       value={searchTermCtx}
                       className="search-input closed-search-input"
                       readOnly={true}
                />
                {searchTermCtx && <button onClick={handleClearSearchTerm}
                                          className="clear">
                    <Icon variant={"medium"} className={"close-icon"} color={"secondaryFont"}>close-line</Icon>
                </button>}
            </SearchContain>
        </SearchBox>
    );

    const mobileSearch = (
        <MobileSearch>
            <Icon variant="medium" color={"darkestGrey"} className="search-icon">search-line</Icon>
            <input type="text"
                   placeholder={messagesQuery.data?.messages["search"]}
                   onClick={() => setShowMobileSearch(true)}
                   value={searchTermCtx}
                   className="show-search"
                   readOnly={true}
            />

            <Offcanvas show={showMobileSearch} onHide={() => setShowMobileSearch(false)}>
                <Offcanvas.Header closeButton>
                </Offcanvas.Header>
                <Offcanvas.Body>
                    {searchBox}
                    {results}
                </Offcanvas.Body>
            </Offcanvas>
        </MobileSearch>
    );

    return (
        <Container>
            <div className="normal-search">
                {!showSearch && dummySearchBox}

                {showSearch && <div className="mask" onClick={() => {
                    setShowSearch(false);
                    clearAllBodyScrollLocks();
                }}></div>}

                {showSearch &&
                    <ResultsContain id="containModal">
                        {searchBox}
                        {results}
                    </ResultsContain>
                }
            </div>

            {props.isMobileSearch &&
                <div className="mobile-search">
                    {mobileSearch}
                </div>}
        </Container>
    );
}

export default SearchAutocomplete;
